import { getPlatformName } from '../config/platforms';
import type { PlatformRow, TopPost } from '../types/stats';

/** A stored timestamp ("YYYY-MM-DD HH:MM:SS" UTC, or ISO) as a short local date and time. */
export const formatDate = (stamp: string | null) => {
  if (!stamp) return 'never';
  const iso = stamp.includes('T') ? stamp : stamp.replace(' ', 'T') + 'Z';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return stamp;
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/**
 * Name the networks a headline figure came from.
 *
 * "1 comment" with nothing beside it sends you hunting through the platform breakdown and the
 * per-post list to find where it happened — and a single comment is invisible in a bar chart
 * scaled to a platform with thirty likes. The attribution was always on the page; it was not
 * where the question gets asked.
 *
 * Nothing is shown for a figure of zero: there is no source to name, and "from nowhere" is
 * noise on three tiles out of four when a workspace is quiet.
 */
/**
 * Append the organic/paid split to a tile's source line.
 *
 * The tile shows the combined figure, because that is what the networks themselves display.
 * Saying which part came from the post and which from an advert is what stops a combined
 * number being read as organic reach — and it is the question "was the boost worth it"
 * depends on.
 */

/**
 * Append the organic/paid split to a tile's source line.
 *
 * The tile shows the combined figure, because that is what the networks themselves display.
 * Saying which part came from the post and which from an advert is what stops a combined
 * number being read as organic reach — and it is the question "was the boost worth it"
 * depends on.
 */
export const withPaid = (base: string | null, organic?: number, paid?: number) => {
  if (!paid) return base;
  // `base` names where the organic part came from, so it belongs to "from posts". Put first
  // and on its own it read "all from X · 1 from posts + 1 from ads" when the ad's comment was
  // on Instagram (2026-10-13).
  let posts = `${(organic || 0).toLocaleString()} from posts`;
  if (base) posts += base.startsWith('all from ') ? ` on ${base.slice('all from '.length)}` : ` (${base})`;
  return `${posts} + ${paid.toLocaleString()} from ads`;
};

/** One post's figure for a metric, organic plus what a boost added — what the tiles count. */
export const withBoost = (post: TopPost, metric: 'likes' | 'comments' | 'shares' | 'impressions') =>
  (post[metric] || 0) + (post[`paid_${metric}`] || 0);

export const sourceOf = (byPlatform: PlatformRow[], metric: 'likes' | 'comments' | 'shares' | 'impressions') => {
  const contributors = byPlatform
    .filter(p => (p[metric] || 0) > 0)
    .sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  if (contributors.length === 0) return null;
  if (contributors.length === 1) return `all from ${getPlatformName(contributors[0].platform)}`;
  return contributors.map(p => `${getPlatformName(p.platform)} ${(p[metric] || 0).toLocaleString()}`).join(' · ');
};
